import React from 'react';
import { highlightSQL } from '../utils/sqlHighlight';

const ArticleRenderer = ({ content }) => {
  // Handle legacy string content
  if (typeof content === 'string') {
    return (
      <div className="article-body">
        {content.split('\n').map((paragraph, index) => (
          <p key={index} style={{ marginBottom: '20px', lineHeight: '1.7' }}>
            {paragraph}
          </p>
        ))}
      </div>
    );
  }

  // Handle new block-based content
  if (!Array.isArray(content)) {
    return <div className="article-body">No content available</div>;
  }

  return (
    <div className="article-body">
      {content.map((block, index) => {
        switch (block.type) {
          case 'heading':
            const HeadingTag = block.content?.level || block.level || 'h2';
            const headingText = block.content?.content || block.content || '';
            return (
              <HeadingTag key={block.id || index} className="article-heading">
                {headingText}
              </HeadingTag>
            );

          case 'text':
            return (
              <div key={block.id || index} className="article-text">
                {block.content.split('\n').map((line, lineIndex) => {
                  if (line.startsWith('• ')) {
                    return (
                      <ul key={lineIndex} style={{ marginBottom: '15px' }}>
                        <li>{line.substring(2)}</li>
                      </ul>
                    );
                  }
                  if (/^\d+\. /.test(line)) {
                    return (
                      <ol key={lineIndex} style={{ marginBottom: '15px' }}>
                        <li>{line.replace(/^\d+\. /, '')}</li>
                      </ol>
                    );
                  }
                  const formattedLine = line
                    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                    .replace(/\*(.*?)\*/g, '<em>$1</em>');
                  
                  return (
                    <p key={lineIndex} 
                       style={{ marginBottom: '15px', lineHeight: '1.7' }}
                       dangerouslySetInnerHTML={{ __html: formattedLine }}
                    />
                  );
                })}
              </div>
            );

          case 'sql':
            return (
              <div key={block.id || index} className="article-sql-block">
                <div className="sql-block-header">
                  <div className="sql-title">
                    <span className="sql-icon">⚡</span>
                    SQL Script
                  </div>
                  <div className="sql-actions">
                    <button
                      onClick={() => navigator.clipboard.writeText(block.content)}
                      className="btn btn-sm btn-secondary"
                    >
                      Copy
                    </button>
                    <button
                      onClick={() => {
                        const blob = new Blob([block.content], { type: 'text/plain' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `script-${Date.now()}.sql`;
                        a.click();
                        URL.revokeObjectURL(url);
                      }}
                      className="btn btn-sm btn-primary"
                    >
                      Download
                    </button>
                  </div>
                </div>
                <div className="sql-content">
                  <pre
                    className="sql-highlighted"
                    dangerouslySetInnerHTML={{
                      __html: highlightSQL(block.content)
                    }}
                  />
                </div>
              </div>
            );

          case 'image':
            if (!block.content?.src) return null;
            return (
              <div key={block.id || index} className="article-image-block">
                <img
                  src={block.content.src}
                  alt={block.content.alt || 'Article image'}
                  className="article-image"
                />
                {block.content.caption && (
                  <div className="image-caption">{block.content.caption}</div>
                )}
              </div>
            );

          case 'table':
            if (!block.content?.headers) return null;
            return (
              <div key={block.id || index} className="article-table-block">
                <table className="article-table">
                  <thead>
                    <tr>
                      {block.content.headers.map((header, headerIndex) => (
                        <th key={headerIndex}>{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {block.content.rows.map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        {row.map((cell, cellIndex) => (
                          <td key={cellIndex}>{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );

          default:
            return null;
        }
      })}
    </div>
  );
};

export default ArticleRenderer;